import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import { IFigureDto } from '../models/IFigureDto';
import FigurService from '../services/figureService';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { getCollectionColors } from '../utils/collectionColors';
import { useConfig } from '../config/env';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { emitAppEvent } from '../events/appEvents';

interface TagsProps {
  imageData: IFigureDto;
  showRemovableTags: boolean;
  collectionId: number; // Collection ID for saving/fetching data
  filterCollectionId?: number; // Optional collection ID for filtering display
  onOverflowClick?: () => void;
  onUpdate?: (data: IFigureDto) => void;
  collections?: Array<{ id: number; name: string; color?: string; isDefault?: boolean }>;
  hideAddTag?: boolean; // If true, hide the add tag input field
  showAllTags?: boolean; // If true, show all tags from all collections (read-only mode)
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#287891',
    },
    secondary: {
      main: '#fae07b',
    },
  },
});

const Tags: React.FC<TagsProps> = ({
  imageData,
  showRemovableTags,
  collectionId,
  filterCollectionId,
  onOverflowClick,
  onUpdate,
  collections = [],
  hideAddTag = false,
  showAllTags = false,
}) => {
  const queryClient = useQueryClient();
  const cfg = useConfig();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [expanded, setExpanded] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [localFigure, setLocalFigure] = useState<IFigureDto>(imageData);
  const lastNonEmptyMetadataRef = useRef<string>(imageData.metadata || '');
  const [displayCustomerMetadata, setDisplayCustomerMetadata] = useState<string>(imageData.customerMetadata || '');
  const tagFieldRef = useRef<HTMLDivElement>(null);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Track if we've received the prop value to prevent flash during view switches
  const hideAddTagRef = useRef(hideAddTag);
  const [shouldShowAddTag, setShouldShowAddTag] = useState(() => {
    // Start hidden if hideAddTag is true, otherwise wait for confirmation
    return hideAddTag === false;
  });

  // Update ref and state when prop changes
  useLayoutEffect(() => {
    hideAddTagRef.current = hideAddTag;
    setShouldShowAddTag(!hideAddTag);
  }, [hideAddTag]);

  // Keep local figure in sync with parent changes
  useEffect(() => {
    setLocalFigure((prev) => ({
      ...prev,
      ...imageData,
      metadata: ((imageData.metadata || '').trim().length > 0
        ? imageData.metadata
        : (prev.metadata || '').trim().length > 0
        ? prev.metadata
        : lastNonEmptyMetadataRef.current) as string,
      // always take latest customer metadata provided by parent
      // If parent explicitly provides empty string (not undefined/null), use it to allow clearing tags
      // Otherwise preserve local value to avoid UI clearing during prop updates
      customerMetadata: (imageData.customerMetadata !== undefined && imageData.customerMetadata !== null
        ? imageData.customerMetadata
        : prev.customerMetadata) as string,
    }));
    if ((imageData.metadata || '').trim().length > 0) lastNonEmptyMetadataRef.current = imageData.metadata as string;
    // Always update displayCustomerMetadata when explicitly provided (including empty string)
    if (imageData.customerMetadata !== undefined && imageData.customerMetadata !== null) {
      setDisplayCustomerMetadata(imageData.customerMetadata);
    }
  }, [imageData]);

  // Split metadata into arrays, filtering empty strings
  const pickNonEmpty = (a?: string | null, b?: string | null, c?: string | null) => {
    if ((a || '').trim().length > 0) return a as string;
    if ((b || '').trim().length > 0) return b as string;
    return (c || '') as string;
  };
  const safeMetadata = pickNonEmpty(localFigure.metadata, imageData.metadata, lastNonEmptyMetadataRef.current);
  const safeCustomerMetadata = (displayCustomerMetadata ?? localFigure.customerMetadata ?? '').toString();
  const bfsMetadataArray = safeMetadata.split('\n').filter(Boolean);

  // Parse customer tags with collection IDs (format: #tag:collectionId or #tag for backward compatibility)
  const customerMetadataArray = safeCustomerMetadata
    .split('#')
    .filter(Boolean)
    .map((tagStr) => {
      const parts = tagStr.trim().split(':');
      if (parts.length === 2) {
        const collectionId = parseInt(parts[1].trim(), 10);
        return { tag: parts[0].trim(), originatorCollection: collectionId };
      }
      return { tag: parts[0].trim(), originatorCollection: undefined };
    });

  const allTagsUnfiltered = [
    ...bfsMetadataArray.map((tag) => ({ tag, type: 'bfs' as const, originatorCollection: undefined })),
    ...customerMetadataArray.map((item) => ({ tag: item.tag, type: 'customer' as const, originatorCollection: item.originatorCollection })),
  ];

  // Filter tags: show BFS tags always, customer tags only if originatorCollection matches filterCollectionId or is undefined
  // If showAllTags is true, show all tags from all collections (read-only mode)
  const filterId = filterCollectionId ?? collectionId;
  const allTags = showAllTags
    ? allTagsUnfiltered // Show all tags when in showAllTags mode
    : allTagsUnfiltered.filter((item) => {
        if (item.type === 'bfs') return true; // Always show BFS tags
        // For customer tags: show if originatorCollection matches filterCollectionId or is undefined (backward compatibility)
        return item.originatorCollection === undefined || item.originatorCollection === filterId;
      });

  // Measure container width
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) setContainerWidth(cr.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Calculate overflow - estimate 70px per tag with scaling
  // Reserve space for: new tag input (~180px) and overflow button when needed (~60px)
  const estimatedTagWidth = 70;
  const newTagInputWidth = shouldShowAddTag ? 180 : 0;
  const overflowButtonWidth = 60;

  // First calculate capacity without overflow button
  const availableWidthWithoutOverflow = Math.max(0, (containerWidth || 0) - newTagInputWidth);
  const perRowWithoutOverflow = Math.max(1, Math.floor(availableWidthWithoutOverflow / estimatedTagWidth));
  const capacityWithoutOverflow = perRowWithoutOverflow;

  // Check if overflow is needed
  const wouldNeedOverflow = !expanded && allTags.length > capacityWithoutOverflow;

  // If overflow is needed, recalculate with overflow button space reserved
  const reservedWidth = newTagInputWidth + (wouldNeedOverflow ? overflowButtonWidth : 0);
  const availableWidth = Math.max(0, (containerWidth || 0) - reservedWidth);
  const perRow = Math.max(1, Math.floor(availableWidth / estimatedTagWidth));
  const capacity = perRow;
  const needsOverflow = !expanded && allTags.length > capacity;

  const dynamicCount = expanded
    ? allTags.length
    : needsOverflow
    ? capacity <= 1
      ? 1 // always show at least one tag alongside +N
      : Math.max(0, capacity - 1)
    : Math.min(allTags.length, capacity);
  const visibleTags = expanded ? allTags : allTags.slice(0, dynamicCount);
  const hiddenCount = expanded ? 0 : Math.max(0, allTags.length - dynamicCount);

  const addTagToMetadata = (tag: string, currentMetadata: string, collectionId: number) => {
    const tagWithCollection = `#${tag}:${collectionId}`;
    return currentMetadata.trim() ? `${currentMetadata.trim()} ${tagWithCollection}` : tagWithCollection;
  };

  // Fetch all available tags using useQuery (similar to TagsFilter)
  const customerId = 1; // TODO: get from context or props
  const { data: availableTags = [], isLoading: isLoadingTags } = useQuery({
    queryKey: ['allTags', customerId],
    queryFn: () => FigurService.getAllTags(customerId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const handleAddTag = async (tagValue: string | null) => {
    const trimmed = tagValue?.trim() || '';
    if (!trimmed) return;
    try {
      // Use filterCollectionId for tag originator, but collectionId for saving/fetching
      const tagOriginatorCollectionId = filterCollectionId ?? collectionId;
      // Optimistic UI update
      const optimisticCustomer = addTagToMetadata(trimmed, displayCustomerMetadata || localFigure.customerMetadata || '', tagOriginatorCollectionId);
      setDisplayCustomerMetadata(optimisticCustomer);
      setLocalFigure((prev) => ({ ...prev, customerMetadata: optimisticCustomer } as IFigureDto));
      setNewTag(''); // Clear the input after adding

      const updated = {
        ...localFigure,
        customerComment: localFigure.customerComment,
        customerMetadata: optimisticCustomer,
      } as IFigureDto;

      await FigurService.saveFigure(updated, collectionId);
      const freshFigure = await FigurService.getFigureByIdAndCollection(localFigure.imageId, collectionId);
      // Preserve BFS metadata if backend omits it in this endpoint
      const merged = {
        ...freshFigure,
        metadata: (freshFigure.metadata || '').trim().length > 0 ? freshFigure.metadata : localFigure.metadata,
      } as IFigureDto;
      setLocalFigure(merged);
      if ((merged.metadata || '').trim().length > 0) lastNonEmptyMetadataRef.current = merged.metadata as string;
      // Always update displayCustomerMetadata when explicitly provided (including empty string)
      if (merged.customerMetadata !== undefined && merged.customerMetadata !== null) {
        setDisplayCustomerMetadata(merged.customerMetadata);
      }
      onUpdate?.(merged);
      emitAppEvent('figureUpdated', merged);

      // Invalidate queries for current collection
      queryClient.invalidateQueries({ queryKey: ['figures-grid', collectionId] });
      queryClient.invalidateQueries({ queryKey: ['figures', collectionId] });

      // Invalidate queries for default collection (Byggforskserien) since backend updates it too
      const defaultCollectionId = Number(cfg?.defaultCollectionId ?? 0);
      if (defaultCollectionId > 0 && defaultCollectionId !== collectionId) {
        queryClient.invalidateQueries({ queryKey: ['figures-grid', defaultCollectionId] });
        queryClient.invalidateQueries({ queryKey: ['figures', defaultCollectionId] });
      }

      // Invalidate queries for all collections this figure belongs to
      if (localFigure.collectionMemberships) {
        Object.keys(localFigure.collectionMemberships).forEach((membershipId) => {
          const membershipCollectionId = Number(membershipId);
          if (membershipCollectionId !== collectionId && membershipCollectionId !== defaultCollectionId) {
            queryClient.invalidateQueries({ queryKey: ['figures-grid', membershipCollectionId] });
            queryClient.invalidateQueries({ queryKey: ['figures', membershipCollectionId] });
          }
        });
      }
    } catch (error) {
      console.error('Error adding tag:', error);
    }
  };

  const handleInputFocus = () => {
    setAutocompleteOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTag(e.target.value);
    setAutocompleteOpen(true);
  };

  const handleClearInput = () => {
    setNewTag('');
    inputRef.current?.focus();
  };

  const handleTagSelectFromList = (tag: string) => {
    void handleAddTag(tag);
    setAutocompleteOpen(false);
  };

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tagFieldRef.current && !tagFieldRef.current.contains(event.target as Node)) {
        setAutocompleteOpen(false);
      }
    };
    if (autocompleteOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [autocompleteOpen]);

  const handleTagRemove = async (event: React.MouseEvent, tag: string, originatorCollection?: number): Promise<void> => {
    event.stopPropagation();
    // Prevent removal if in showAllTags mode (read-only)
    if (showAllTags) return;

    // Remove tag with collection ID if present, otherwise use simple format for backward compatibility
    const formattedTag = originatorCollection !== undefined ? `#${tag.trim()}:${originatorCollection}` : `#${tag.trim()}`;

    if (localFigure.customerMetadata?.includes(formattedTag)) {
      // Optimistic UI update
      const currentCustomer = displayCustomerMetadata || localFigure.customerMetadata || '';
      const nextCustomer = currentCustomer.replace(formattedTag, '').replace(/\s+/g, ' ').trim();
      setDisplayCustomerMetadata(nextCustomer);
      setLocalFigure((prev) => ({ ...prev, customerMetadata: nextCustomer } as IFigureDto));

      const updatedImageData = {
        ...localFigure,
        // Explicitly ensure empty string is sent (not null/undefined) to allow clearing tags
        customerMetadata: nextCustomer || '',
      };
      await FigurService.saveFigure(updatedImageData, collectionId);
      // Fetch the fresh single figure and broadcast to interested views
      const freshFigure = await FigurService.getFigureByIdAndCollection(localFigure.imageId, collectionId);
      const merged = {
        ...freshFigure,
        metadata: (freshFigure.metadata || '').trim().length > 0 ? freshFigure.metadata : localFigure.metadata,
      } as IFigureDto;
      setLocalFigure(merged);
      if ((merged.metadata || '').trim().length > 0) lastNonEmptyMetadataRef.current = merged.metadata as string;
      // Always update displayCustomerMetadata when explicitly provided (including empty string)
      if (merged.customerMetadata !== undefined && merged.customerMetadata !== null) {
        setDisplayCustomerMetadata(merged.customerMetadata);
      }
      onUpdate?.(merged);
      emitAppEvent('figureUpdated', merged);

      // Invalidate queries for current collection
      queryClient.invalidateQueries({ queryKey: ['figures-grid', collectionId] });
      queryClient.invalidateQueries({ queryKey: ['figures', collectionId] });

      // Invalidate queries for default collection (Byggforskserien) since backend updates it too
      const defaultCollectionId = Number(cfg?.defaultCollectionId ?? 0);
      if (defaultCollectionId > 0 && defaultCollectionId !== collectionId) {
        queryClient.invalidateQueries({ queryKey: ['figures-grid', defaultCollectionId] });
        queryClient.invalidateQueries({ queryKey: ['figures', defaultCollectionId] });
      }

      // Invalidate queries for all collections this figure belongs to
      if (localFigure.collectionMemberships) {
        Object.keys(localFigure.collectionMemberships).forEach((membershipId) => {
          const membershipCollectionId = Number(membershipId);
          if (membershipCollectionId !== collectionId && membershipCollectionId !== defaultCollectionId) {
            queryClient.invalidateQueries({ queryKey: ['figures-grid', membershipCollectionId] });
            queryClient.invalidateQueries({ queryKey: ['figures', membershipCollectionId] });
          }
        });
      }
    }
  };

  // Never early-return; keep input visible even if no tags are present

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: '100%' }}>
        <Box ref={containerRef}>
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexWrap: expanded ? 'wrap' : 'nowrap',
              gap: 0.125,
              justifyContent: 'flex-start',
              width: '100%',
              alignItems: 'center',
              // Allow dropdown to be visible when open
              overflow: expanded || autocompleteOpen ? 'visible' : 'hidden',
            }}>
            {visibleTags.map((item, idx) => {
              const { tag, type, originatorCollection } = item;
              const isBfs = type === 'bfs';

              // Get collection color for tags based on originator collection
              let tagBgColor: string | undefined;
              let tagTextColor: string | undefined;

              if (showAllTags) {
                // When showing all tags, color each tag based on its originator collection
                if (isBfs) {
                  // BFS tags use primary color (default blue)
                  tagBgColor = undefined; // Will use MUI primary color
                  tagTextColor = undefined;
                } else if (originatorCollection !== undefined) {
                  // Find collection in collections array or figure's collectionMemberships
                  const targetCollectionId = originatorCollection;
                  let collection = collections.find((c) => c.id === targetCollectionId);

                  // If not found in collections prop, try to get from figure's collectionMemberships
                  if (!collection && imageData.collectionMemberships && imageData.collectionMemberships[targetCollectionId]) {
                    const collectionName = imageData.collectionMemberships[targetCollectionId];
                    collection = { id: targetCollectionId, name: collectionName };
                  }

                  if (collection) {
                    const isDefaultCollection =
                      collection.id === Number(cfg?.defaultCollectionId ?? 0) || collection.isDefault || collection.name === 'Byggforskserien';
                    const colors = getCollectionColors({
                      id: collection.id,
                      name: collection.name,
                      color: collection.color,
                      isDefault: isDefaultCollection,
                    });
                    tagBgColor = colors.bg;
                    tagTextColor = colors.color;
                  } else {
                    // Fallback: use collection ID to generate a color
                    const colors = getCollectionColors({ id: targetCollectionId, name: `Collection ${targetCollectionId}` });
                    tagBgColor = colors.bg;
                    tagTextColor = colors.color;
                  }
                } else {
                  // Tag without originator collection - use current collection or default
                  const targetCollectionId = collectionId;
                  const collection = collections.find((c) => c.id === targetCollectionId);
                  if (collection) {
                    const isDefaultCollection =
                      collection.id === Number(cfg?.defaultCollectionId ?? 0) || collection.isDefault || collection.name === 'Byggforskserien';
                    const colors = getCollectionColors({
                      id: collection.id,
                      name: collection.name,
                      color: collection.color,
                      isDefault: isDefaultCollection,
                    });
                    tagBgColor = colors.bg;
                    tagTextColor = colors.color;
                  }
                }
              } else if (!isBfs && collections.length > 0) {
                // Normal mode: Use originatorCollection if available, otherwise fall back to current collection
                const targetCollectionId = originatorCollection !== undefined ? originatorCollection : collectionId;
                const collection = collections.find((c) => c.id === targetCollectionId);
                if (collection) {
                  const isDefaultCollection =
                    collection.id === Number(cfg?.defaultCollectionId ?? 0) || collection.isDefault || collection.name === 'Byggforskserien';
                  const colors = getCollectionColors({
                    id: collection.id,
                    name: collection.name,
                    color: collection.color,
                    isDefault: isDefaultCollection,
                  });
                  tagBgColor = colors.bg;
                  tagTextColor = colors.color;
                }
              }

              const sxProps: Record<string, unknown> = {
                borderRadius: '20px',
                justifyContent: 'flex-start',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                minWidth: 'fit-content',
                padding: isBfs ? '1px 10px' : '1px 8px',
                height: isBfs ? '20px' : '18px',
                mx: 0,
                my: 0.25,
                transform: 'scale(0.8)',
                transformOrigin: 'left center',
                '& .MuiButton-endIcon': {
                  ml: 1,
                  mr: -0.5,
                },
              };

              if (tagBgColor) sxProps.bgcolor = tagBgColor;
              if (tagTextColor) sxProps.color = tagTextColor;

              return (
                <Button
                  key={`${type}-${tag}-${idx}`}
                  variant="contained"
                  size="small"
                  color={isBfs ? 'primary' : 'secondary'}
                  onClick={
                    showAllTags ? undefined : !isBfs && showRemovableTags ? (event) => handleTagRemove(event, tag, originatorCollection) : undefined
                  }
                  endIcon={
                    showAllTags ? undefined : !isBfs && showRemovableTags ? (
                      <CloseIcon sx={{ fontSize: '0.4rem', ml: -0.5, opacity: 0.6 }} />
                    ) : undefined
                  }
                  sx={{ ...sxProps, ...(showAllTags ? { cursor: 'default', pointerEvents: 'none' } : {}) }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', pl: 1, whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                    {tag}
                  </Typography>
                </Button>
              );
            })}
            {hiddenCount > 0 && (
              <Tooltip title={`Vis ${hiddenCount} flere`} arrow>
                <Button
                  variant="contained"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onOverflowClick) {
                      onOverflowClick();
                    } else {
                      setExpanded(true);
                    }
                  }}
                  sx={{
                    borderRadius: '20px',
                    minWidth: 'fit-content',
                    padding: '1px 10px',
                    height: '20px',
                    mx: 0,
                    my: 0.25,
                    transform: 'scale(0.8)',
                    transformOrigin: 'left center',
                    bgcolor: 'grey.300',
                    color: 'text.primary',
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: 'grey.400',
                    },
                  }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                    +{hiddenCount}
                  </Typography>
                </Button>
              </Tooltip>
            )}

            {/* Inline add-tag field with Autocomplete dropdown */}
            {shouldShowAddTag && (
              <Box ref={tagFieldRef} sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5, position: 'relative', flexShrink: 0 }}>
                <TextField
                  inputRef={inputRef}
                  value={newTag}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newTag.trim()) {
                      e.preventDefault();
                      void handleAddTag(newTag);
                    }
                  }}
                  variant="standard"
                  placeholder="Tag..."
                  size="small"
                  sx={{
                    minWidth: '14ch',
                    maxWidth: '14ch',
                    '& .MuiInputBase-root': {
                      display: 'flex',
                      alignItems: 'center',
                      height: 24,
                      backgroundColor: 'transparent',
                      color: 'inherit',
                      px: 0.5,
                      position: 'relative',
                      top: 0,
                    },
                    '& .MuiInput-underline:before': {
                      borderBottomColor: (theme) => theme.palette.grey[600],
                    },
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                      borderBottomColor: (theme) => theme.palette.grey[700],
                    },
                    '& .MuiInput-underline:after': {
                      borderBottomColor: (theme) => theme.palette.grey[800],
                    },
                    '& .MuiInputBase-input': {
                      padding: '0 !important',
                      fontSize: '0.875rem',
                      paddingRight: '48px !important',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ position: 'absolute', right: 0 }}>
                        {newTag && (
                          <IconButton size="small" onClick={handleClearInput} sx={{ p: 0.25, mr: 0.5 }} aria-label="Clear">
                            <CloseIcon sx={{ fontSize: '0.875rem' }} />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => setAutocompleteOpen(!autocompleteOpen)} sx={{ p: 0.25 }} aria-label="Show tags">
                          <ArrowDropDownIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ 'aria-label': 'Legg til tag' }}
                />
                {/* Autocomplete dropdown */}
                {autocompleteOpen && (
                  <Paper
                    elevation={3}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      mt: 0.5,
                      minWidth: '200px',
                      maxWidth: '300px',
                      maxHeight: 300,
                      overflow: 'hidden',
                      zIndex: 1300,
                    }}>
                    <List
                      sx={{
                        py: 0,
                        maxHeight: 300,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                      }}>
                      {isLoadingTags ? (
                        <ListItem disablePadding>
                          <ListItemButton disabled>
                            <ListItemText primary="Laster tags..." primaryTypographyProps={{ fontSize: '0.875rem' }} />
                          </ListItemButton>
                        </ListItem>
                      ) : availableTags
                          .filter((tag) => {
                            // Filter out tags that are already on this figure
                            const tagLower = tag.toLowerCase();
                            const existingTags = allTags.map((t) => t.tag.toLowerCase());
                            return !existingTags.includes(tagLower);
                          })
                          .filter((tag) => {
                            // Filter by input value
                            if (!newTag.trim()) return true;
                            return tag.toLowerCase().includes(newTag.toLowerCase());
                          }).length > 0 ? (
                        availableTags
                          .filter((tag) => {
                            const tagLower = tag.toLowerCase();
                            const existingTags = allTags.map((t) => t.tag.toLowerCase());
                            return !existingTags.includes(tagLower);
                          })
                          .filter((tag) => {
                            if (!newTag.trim()) return true;
                            return tag.toLowerCase().includes(newTag.toLowerCase());
                          })
                          .map((tag) => {
                            const isBfsTag = bfsMetadataArray.some((t) => t.toLowerCase() === tag.toLowerCase());
                            return (
                              <ListItem key={tag} disablePadding>
                                <ListItemButton
                                  onClick={() => handleTagSelectFromList(tag)}
                                  sx={{
                                    py: 1,
                                    px: 1.5,
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                  }}>
                                  <ListItemText
                                    primary={tag}
                                    primaryTypographyProps={{
                                      fontSize: '0.875rem',
                                      fontWeight: 500,
                                      color: isBfsTag ? 'primary.main' : 'text.primary',
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })
                      ) : (
                        <ListItem disablePadding>
                          <ListItemButton disabled>
                            <ListItemText primary="Ingen tags funnet" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                          </ListItemButton>
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Tags;
